import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Wrench, Activity, BarChart3, Trash2, Fuel, Pencil, MapPin, User } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { equipmentAPI } from '../../services/api';
import { format } from 'date-fns';
import { PageTabRow } from '../../components/ui';
import ServiceHistory from './ServiceHistory';
import RunLogList from './RunLogList';
import FuelLogList from './FuelLogList';
import AddService from './AddService';
import AddRunLog from './AddRunLog';
import AddFuelLog from './AddFuelLog';
import AssetTabs from '../assets/AssetTabs';
import { getEffectiveNextServiceDue } from './serviceDue';
import { resolveUploadUrl } from '../../utils/uploadUrl';

const STATUS_COLORS = { ACTIVE: 'bg-ga-success/20 text-ga-success', UNDER_REPAIR: 'bg-ga-text-subtle/20 text-ga-text-muted', OUT_OF_SERVICE: 'bg-ga-danger/20 text-ga-danger' };

const formatMoney = (value) => (
  value != null && value !== '' ? `₹${Number(value).toLocaleString()}` : '—'
);

export default function EquipmentDetails() {
  const { id } = useParams();
  const location = useLocation();
  const assetMode = location.pathname.includes('/dashboard/assets/');
  const basePath = assetMode ? '/dashboard/assets/fixed' : '/dashboard/operations/equipment';
  const [tab, setTab] = useState('overview');
  const [equipment, setEquipment] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddService, setShowAddService] = useState(false);
  const [showAddRunLog, setShowAddRunLog] = useState(false);
  const [showAddFuelLog, setShowAddFuelLog] = useState(false);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const eqRes = await equipmentAPI.get(id);
      setEquipment(eqRes.data);
      try {
        const analyticsRes = await equipmentAPI.getAnalytics(id);
        setAnalytics(analyticsRes?.data || {});
      } catch {
        setAnalytics({});
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDetails(); }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) return;
    try {
      await equipmentAPI.delete(id);
      window.location.href = basePath;
    } catch (e) {
      alert(e.message);
    }
  };

  const onServiceAdded = () => { setShowAddService(false); fetchDetails(); };
  const onRunLogAdded = () => { setShowAddRunLog(false); fetchDetails(); };
  const onFuelLogAdded = () => { setShowAddFuelLog(false); fetchDetails(); };

  if (loading || !equipment) {
    return (
      <DashboardLayout>
        <div className="py-12 text-center text-ga-text-muted">Loading...</div>
      </DashboardLayout>
    );
  }

  const hasFuel = equipment.fuel_type &&
    /diesel|petrol/i.test(equipment.fuel_type);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Wrench },
    { id: 'service', label: 'Service History', icon: Activity },
    { id: 'runlog', label: 'Run Logs', icon: Wrench },
    ...(hasFuel ? [{ id: 'fuellog', label: 'Fuel Log', icon: Fuel }] : []),
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={basePath} aria-label="Back to asset list" className="p-2 rounded-lg text-ga-text-primary hover:bg-ga-bg-surface-subtle">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-ga-text-primary">{equipment.equipment_name}</h1>
              <p className="text-ga-text-muted font-mono text-sm">{equipment.asset_code}</p>
            </div>
            {equipment.disposal_status ? (
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-ga-text-subtle/20 text-ga-text-muted">
                {equipment.disposal_status.replace('_', ' ')}
              </span>
            ) : (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[equipment.status] || 'bg-ga-bg-surface-subtle text-ga-text-muted'}`}>
                {equipment.status?.replace('_', ' ')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link to={`${basePath}/${id}/edit`} className="ga-btn-ghost inline-flex items-center gap-2 px-3 py-2">
              <Pencil className="h-4 w-4" />
              Edit
            </Link>
            <button onClick={handleDelete} aria-label="Delete equipment" className="p-2 text-ga-danger hover:bg-ga-danger/10 rounded-lg">
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {assetMode && <AssetTabs active="fixed" />}

        <PageTabRow tabs={tabs} active={tab} onChange={setTab} ariaLabel="Asset detail sections" />

        {tab === 'overview' && (
          <div className="ga-card space-y-6">
            {equipment.photo_url && (
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-2">Intake photo</h3>
                <a
                  href={resolveUploadUrl(equipment.photo_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <img
                    src={resolveUploadUrl(equipment.photo_url)}
                    alt={`${equipment.equipment_name} intake`}
                    className="max-h-56 rounded-lg border border-ga-border-subtle object-contain"
                  />
                </a>
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location</h3>
                <p className="text-ga-text-primary">{equipment.location || '-'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1 flex items-center gap-1"><User className="h-3 w-3" /> Assigned to</h3>
                <p className="text-ga-text-primary">{equipment.assigned_staff_name || 'Unassigned'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Type</h3>
                <p className="text-ga-text-primary">{equipment.equipment_type || '-'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Purchase</h3>
                <p className="text-ga-text-primary">{equipment.purchase_date ? format(new Date(equipment.purchase_date), 'dd MMM yyyy') : '—'}</p>
                <p className="text-ga-text-primary">{formatMoney(equipment.purchase_cost)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Warranty</h3>
                <p className="text-ga-text-primary">{equipment.warranty_expiry ? format(new Date(equipment.warranty_expiry), 'dd MMM yyyy') : '-'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Total Runtime</h3>
                <p className="text-ga-text-primary font-semibold">{(equipment.total_runtime || 0).toFixed(1)} hrs</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Total Maintenance Cost</h3>
                <p className="text-ga-text-primary font-semibold">₹{(equipment.total_maintenance_cost || 0).toLocaleString()}</p>
              </div>
              {equipment.disposal_status && (
                <>
                  <div>
                    <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Disposal date</h3>
                    <p className="text-ga-text-primary">{equipment.disposal_date ? format(new Date(equipment.disposal_date), 'dd MMM yyyy') : '-'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="text-sm font-medium text-ga-text-subtle mb-1">Disposal reason</h3>
                    <p className="text-ga-text-primary">{equipment.disposal_reason || '-'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {tab === 'service' && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setShowAddService(true)} className="ga-btn-primary">
                <Plus className="h-5 w-5" />
                Add Service
              </button>
            </div>
            <ServiceHistory equipmentId={id} onRefresh={fetchDetails} />
            {showAddService && <AddService equipmentId={id} onClose={() => setShowAddService(false)} onSaved={onServiceAdded} />}
          </div>
        )}

        {tab === 'runlog' && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setShowAddRunLog(true)} className="ga-btn-primary">
                <Plus className="h-5 w-5" />
                Add Run Log
              </button>
            </div>
            <RunLogList equipmentId={id} onRefresh={fetchDetails} />
            {showAddRunLog && <AddRunLog equipmentId={id} onClose={() => setShowAddRunLog(false)} onSaved={onRunLogAdded} />}
          </div>
        )}

        {tab === 'fuellog' && hasFuel && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => setShowAddFuelLog(true)} className="ga-btn-primary">
                <Plus className="h-5 w-5" />
                Add Fuel Log
              </button>
            </div>
            <FuelLogList equipmentId={id} fuelType={equipment.fuel_type} onRefresh={fetchDetails} />
            {showAddFuelLog && (
              <AddFuelLog
                equipmentId={id}
                fuelType={equipment.fuel_type}
                onClose={() => setShowAddFuelLog(false)}
                onSaved={onFuelLogAdded}
              />
            )}
          </div>
        )}

        {tab === 'analytics' && (
          <div className="space-y-6">
            {/* Summary KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="ga-card p-4">
                <p className="text-xs text-ga-text-subtle">Total Runtime</p>
                <p className="text-2xl font-bold text-ga-text-primary">{(equipment.total_runtime || 0).toFixed(1)} <span className="text-sm font-normal">hrs</span></p>
              </div>
              <div className="ga-card p-4">
                <p className="text-xs text-ga-text-subtle">Total Maintenance</p>
                <p className="text-2xl font-bold text-ga-warning">₹{(equipment.total_maintenance_cost || 0).toLocaleString()}</p>
              </div>
              <div className="ga-card p-4">
                <p className="text-xs text-ga-text-subtle">Cost per Hour</p>
                <p className="text-2xl font-bold text-ga-primary">₹{(analytics?.costPerHour || 0).toFixed(2)}</p>
              </div>
              <div className="ga-card p-4">
                <p className="text-xs text-ga-text-subtle">Next Service</p>
                {(() => {
                  const { date: nextDue, derived } = getEffectiveNextServiceDue(equipment);
                  return (
                    <p className="text-lg font-bold text-ga-text-primary">
                      {nextDue ? format(nextDue, 'dd MMM yyyy') : 'Not set'}
                      {derived && nextDue && (
                        <span className="ml-1 text-xs font-normal text-ga-text-subtle" title="Estimated from service interval and purchase/creation date">
                          (est.)
                        </span>
                      )}
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Maintenance Cost by Month */}
            <div className="ga-card p-6">
              <h3 className="font-semibold text-ga-text-primary mb-4">Maintenance Cost by Month</h3>
              {(analytics?.maintenanceCostByMonth || []).length === 0 ? (
                <div className="py-8 text-center text-ga-text-subtle text-sm">
                  No maintenance costs recorded yet. Add a service with cost to see trends.
                </div>
              ) : (
                <div className="flex items-end gap-2 h-40">
                  {(analytics.maintenanceCostByMonth || []).slice(0, 12).reverse().map((m) => {
                    const maxVal = Math.max(1, ...(analytics.maintenanceCostByMonth || []).map((x) => parseFloat(x.total) || 0));
                    const val = parseFloat(m.total) || 0;
                    return (
                      <div key={m.month} className="flex-1 flex flex-col items-center group" title={`₹${val.toLocaleString()}`}>
                        <div className="text-xs text-ga-text-subtle opacity-0 group-hover:opacity-100 mb-1 transition-opacity">
                          ₹{val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val}
                        </div>
                        <div
                          className="w-full bg-ga-primary rounded-t min-h-[4px]"
                          style={{ height: `${Math.max(4, (val / maxVal) * 100)}%` }}
                        />
                        <span className="text-xs text-ga-text-subtle mt-1">
                          {m.month ? format(new Date(m.month), 'MMM') : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Runtime Trend (30 days) */}
            <div className="ga-card p-6">
              <h3 className="font-semibold text-ga-text-primary mb-4">Runtime Trend (30 days)</h3>
              {(analytics?.runtimeTrend || []).length === 0 ? (
                <div className="py-8 text-center text-ga-text-subtle text-sm">
                  No run logs recorded yet. Add run logs to see daily usage trends.
                </div>
              ) : (
                <div className="flex items-end gap-1 h-32">
                  {(analytics.runtimeTrend || []).map((r) => {
                    const maxH = Math.max(1, ...(analytics.runtimeTrend || []).map((x) => parseFloat(x.hours) || 0));
                    const hrs = parseFloat(r.hours) || 0;
                    return (
                      <div key={r.log_date} className="flex-1 flex flex-col items-center group" title={`${r.log_date}: ${hrs} hrs`}>
                        <div
                          className="w-full bg-teal-500 rounded-t min-h-[2px] group-hover:bg-teal-400 transition-colors"
                          style={{ height: `${Math.max(2, (hrs / maxH) * 100)}%` }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Equipment info */}
            <div className="ga-card p-6">
              <h3 className="font-semibold text-ga-text-primary mb-4">Equipment Info</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {equipment.brand && <div><span className="text-ga-text-subtle">Brand:</span> <span className="font-medium">{equipment.brand}</span></div>}
                {equipment.model && <div><span className="text-ga-text-subtle">Model:</span> <span className="font-medium">{equipment.model}</span></div>}
                {equipment.serial_number && <div><span className="text-ga-text-subtle">Serial:</span> <span className="font-medium font-mono">{equipment.serial_number}</span></div>}
                {equipment.fuel_type && <div><span className="text-ga-text-subtle">Fuel:</span> <span className="font-medium capitalize">{equipment.fuel_type}</span></div>}
                {equipment.service_interval_days && <div><span className="text-ga-text-subtle">Service Every:</span> <span className="font-medium">{equipment.service_interval_days} days</span></div>}
                {equipment.service_interval_hours && <div><span className="text-ga-text-subtle">Service Every:</span> <span className="font-medium">{equipment.service_interval_hours} hrs</span></div>}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
